import { Loader } from '@openx/components-core/lib/Loader/Loader.tsx';
import { Intersect } from '@openx/types/targeting/targetingValuesTypes.ts';
import {
  type TechnologyAndDevicesFormState,
  TechnologyAndDevicesOption,
} from '@openx/types/targeting/technologyAndDevices.ts';
import { noop } from '@openx/utils/noop.ts';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTargetingContext } from '../../../utils/context/TargetingContext.tsx';
import { FiltersTitle } from '../../shared/FiltersTitle/FiltersTitle.tsx';
import { SelectedItems } from '../../shared/SelectedItems/SelectedItems.tsx';
import { Switch } from '../../shared/Switch/Switch.tsx';
import type { TargetingItemsProps } from '../../types.ts';
import { MobileCarrierOptions, MobileCarrierWifiOption } from '../constants.ts';

export const MobileCarrierItems = ({
  targetingParams,
  readonly = true,
  onChange = noop,
  disabledReasonMessage,
}: TargetingItemsProps<TechnologyAndDevicesFormState>) => {
  const { t } = useTranslation();
  const { useTechnologyOptionsFetch } = useTargetingContext();
  const { options, isLoading } = useTechnologyOptionsFetch(TechnologyAndDevicesOption.MOBILE_CARRIER, true);

  const { op, val } = targetingParams[TechnologyAndDevicesOption.MOBILE_CARRIER];
  const switchState = op === Intersect.NOT_INTERSECTS;
  const filtersTitle = switchState ? t('Is not any of') : t('Is any of');
  const isMobileCarierWifiOnlySelected =
    String(op) === MobileCarrierWifiOption.wifi && Array.from(val).join('') === MobileCarrierOptions.isWifiOnly;

  const handleChange = useCallback(
    (values: string[]) => {
      const selected = new Set(values);

      onChange(state => ({
        ...state,
        [TechnologyAndDevicesOption.MOBILE_CARRIER]: { op, val: selected },
      }));
    },
    [onChange, op]
  );

  const onSwitchChange = useCallback(() => {
    onChange(state => ({
      ...state,
      [TechnologyAndDevicesOption.MOBILE_CARRIER]: {
        op: op === Intersect.INTERSECTS ? Intersect.NOT_INTERSECTS : Intersect.INTERSECTS,
        val,
      },
    }));
  }, [onChange, op, val]);

  const selectedItems = useMemo(() => {
    return [...val];
  }, [val]);

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div data-test="sub-section">
      <FiltersTitle
        onClear={() => handleChange([])}
        title={t('Mobile Carrier [ {count} ]', { count: val.size })}
        readonly={readonly}
        disabledReasonMessage={disabledReasonMessage}
      />

      {!readonly && !isMobileCarierWifiOnlySelected && (
        <Switch
          switchState={switchState}
          onSwitchChange={onSwitchChange}
          switchOffLabel={t('Is any of')}
          switchOnLabel={t('Is not any of')}
        />
      )}

      <SelectedItems
        filtersTitle={readonly && !isMobileCarierWifiOnlySelected ? filtersTitle : ''}
        isAddIcon={!switchState}
        selectedItems={selectedItems}
        readonly={readonly}
        handleChange={handleChange}
        options={options}
        dataTest={'mobile-carrier'}
      />
    </div>
  );
};
